<script lang="ts" setup>
import { ItemHelper as ItemHelperClass } from "../../helpers/item.helper";
import { CartService } from "../../services/cart.service";
import { ProductsService } from "../../services/products.service";
import { TranslationService } from "../../services/translation.service";
import { inject } from "vue";

const {
    currentBrand,
    fulltext: fullText,
    take,
    skip,
    addToBasketUrl,
} = defineProps({
    currentBrand: {
        type: Number,
        required: true,
    },
    fulltext: {
        type: String,
        required: false,
        default: "",
    },
    skip: {
        type: Number,
        required: false,
        default: 0,
    },
    take: {
        type: Number,
        required: false,
        default: 51,
    },
    actionUrl: {
        type: String,
        required: true,
    },
    addToBasketUrl: {
        type: String,
        required: true,
    },
});

const productsSvc = inject<ProductsService>("productsSvc")!;
const cartSvc = inject<CartService>("cartSvc")!;
const ItemHelper = inject<ItemHelperClass>("ItemHelper")!;
const t = inject<TranslationService>("translationSvc")!.t;
// console.log(productsSvc.brands[currentBrand]);

productsSvc.init(fullText, productsSvc.brands[currentBrand], skip, take);
</script>

<template>
    <div class="products-list" v-if="productsSvc.isInited">
        <div class="row">
            <div class="col-lg-offset-1 col-lg-11">
                <div
                    class="flex md:flex-row flex-col md:justify-between gap-4 md:items-center bg-white shadow rounded p-8 mb-4"
                >
                    <div class="hidden md:block">
                        {{ t("PRODUCTS_SEARCH_RESULT") }}
                    </div>
                    <div class="">
                        <form
                            method="GET"
                            :action="actionUrl"
                            id="searchform"
                            @submit.prevent="
                                productsSvc.loadItems(
                                    productsSvc.searchFulltext,
                                    productsSvc.brand!.Code,
                                    take,
                                    true,
                                    false,
                                    true,
                                )
                            "
                        >
                            <input
                                class="input-text-input !px-5 !py-3 min-w-16 bg-grey-50 rounded-s h-10"
                                type="text"
                                name="term"
                                :placeholder="t('PRODUCTS_SHOP_SEARCH')"
                                v-model="productsSvc.searchFulltext"
                            />
                        </form>
                    </div>
                    <div
                        class=""
                        v-text="
                            productsSvc.getQueryResultsCountStr(
                                t('PRODUCTS_RECORD'),
                                t('PRODUCTS_RECORDS'),
                            )
                        "
                    ></div>
                    <div class="" @click.stop>
                        <div
                            class="dropdown-cb vertical-center relative"
                            :class="{ 'dropdown-open': productsSvc.drpBrand }"
                        >
                            <div
                                class="flex flex-row items-center gap-2"
                                @click="
                                    productsSvc.drpBrand = !productsSvc.drpBrand
                                "
                            >
                                <span
                                    :class="
                                        productsSvc.dropdownLoading
                                            ? 'loading-animation'
                                            : ''
                                    "
                                    :style="
                                        productsSvc.dropdownLoading
                                            ? { color: 'transparent' }
                                            : {}
                                    "
                                    v-text="productsSvc.brand!.Name"
                                ></span>
                                <i
                                    class="text-primary fas"
                                    :class="
                                        productsSvc.drpBrand
                                            ? 'fa-chevron-up'
                                            : 'fa-chevron-down'
                                    "
                                ></i>
                            </div>
                            <ul
                                v-show="productsSvc.drpBrand"
                                class="absolute z-10 bg-white shadow-md rounded mt-1 right-0 top-8 p-4 max-h-80 overflow-y-auto"
                            >
                                <li
                                    class=""
                                    v-for="(b, key) in productsSvc.brands"
                                >
                                    <div
                                        @click="
                                            productsSvc.selectBrand(
                                                key,
                                                false,
                                                true,
                                            )
                                        "
                                        class="py-2 cursor-pointer"
                                        v-text="b.Name"
                                    ></div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Records found (mobile/tablet only) -->
                <div class="block md:hidden">
                    <span
                        v-text="
                            productsSvc.getQueryResultsCountStr(
                                t('PRODUCTS_FOUND_ITEM'),
                                t('PRODUCTS_FOUND_ITEMS'),
                            )
                        "
                    ></span>
                    "{{ fulltext }}"
                </div>

                <!-- Table header -->
                <div class="hidden md:block mt-12 mb-12">
                    <h2 class="inline-block w-[28%] text-f5 font-bold">
                        {{ t("PRODUCT_DESCRIPTION") }}
                    </h2>
                    <h2 class="inline-block w-[17%] text-f5 font-bold">
                        {{ t("ARTICLE_NUMBER") }}
                    </h2>
                    <h2 class="inline-block w-[17%] text-f5 font-bold">
                        {{ t("BRAND") }}
                    </h2>
                    <h2 class="inline-block w-[15%] text-f5 font-bold">
                        {{ t("AVAILABILITY") }}*
                    </h2>
                    <h2 class="inline-block w-[12%] text-f5 font-bold">CHF</h2>
                    <h2 class="inline-block w-[11%] text-f5 font-bold">
                        {{ t("QUANTITY") }}
                    </h2>
                </div>

                <div
                    class="py-4 hover:bg-grey-30 md:block flex flex-wrap gap-4"
                    v-for="item in productsSvc.itemData.Items"
                >
                    <div class="inline-block md:w-[28%] align-top">
                        <div class="md:hidden block bold mb-2 font-bold">
                            {{ t("PRODUCT_DESCRIPTION") }}
                        </div>
                        <div v-text="item.ItemName"></div>
                        <span class="mr-2">pack size:</span>
                        <span v-text="item.Packsize"></span>

                        <div :style="{ display: item.CAS ? 'block' : 'none' }">
                            <span class="mr-2">CAS:</span>
                            <a
                                class="underline"
                                href="{{ craft.app.config.custom.CAS_URL ~ '{{ item.CAS }}' }}"
                                target="_blank"
                                v-text="item.CAS"
                            ></a>
                        </div>
                        <div :style="{ display: item.MSDS ? 'block' : 'none' }">
                            <a
                                class="underline"
                                :href="item.MSDS"
                                target="_blank"
                                >msds</a
                            >
                        </div>
                    </div>

                    <div class="inline-block md:w-[17%] align-top">
                        <div class="md:hidden block bold mb-2">
                            {{ t("ARTICLE_NUMBER") }}
                        </div>
                        <div v-text="item.ItemCode"></div>
                    </div>

                    <div class="inline-block md:w-[17%] align-top">
                        <div class="md:hidden block bold mb-2">
                            {{ t("BRAND") }}
                        </div>
                        <div class="" v-text="item.BrandName"></div>
                    </div>
                    <div class="inline-block md:w-[15%] align-top">
                        <div class="md:hidden block bold mb-2">
                            {{ t("AVAILABILITY") }}*
                        </div>
                        <div
                            class=""
                            v-text="
                                ItemHelper.availOrLeadText(
                                    item.Availability,
                                    item.LeadTime,
                                )
                            "
                        ></div>
                    </div>

                    <div class="inline-block md:w-[12%] align-top">
                        <div class="md:hidden block font-bold mb-2">chf</div>
                        <div
                            class="font-bold"
                            :class="{
                                bold: true,
                                'color-specialPrice':
                                    cartSvc.hasItemSpecialPrice(item),
                            }"
                            v-text="cartSvc.getItemSinglePrice(item).toFixed(2)"
                        ></div>
                    </div>
                    <div class="inline-block md:w-[11%] align-top">
                        <form :action="addToBasketUrl" method="POST" class="">
                            <input
                                type="hidden"
                                name="item"
                                :value="JSON.stringify(item)"
                            />

                            <div class="flex gap-2 items-center w-full">
                                <input
                                    class="!px-2 !py-3 bg-grey-50 rounded-s h-10 flex-shrink w-14"
                                    type="number"
                                    name="quantity"
                                    min="1"
                                    value="1"
                                />

                                <button
                                    type="submit"
                                    class="btn-cb btn-small h-10 w-10"
                                >
                                    <i class="fas fa-shopping-cart"></i>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- Message: no items in cart -->
                <div
                    class="row products-list-row"
                    v-if="
                        productsSvc.isLoadingProducts ||
                        productsSvc.isLoadingMoreProducts
                    "
                >
                    <div
                        class="col-xs-offset-4 col-xs-8 col-sm-offset-2 col-sm-10 col-md-offset-0 col-md-12 loading-animation"
                    >
                        <br />
                    </div>
                </div>

                <!-- Message: no items in cart -->
                <div
                    class="row products-list-row"
                    v-if="
                        !(
                            productsSvc.isLoadingProducts ||
                            productsSvc.isLoadingMoreProducts
                        ) &&
                        productsSvc.itemData.Items.length == 0 &&
                        productsSvc.itemData.ResponseStatus != 500
                    "
                    v-cloak
                >
                    <div
                        class="col-xs-offset-4 col-xs-8 col-sm-offset-2 col-sm-10 col-md-offset-0 col-md-12"
                    >
                        <span>{{ t("NO_PRODUCTS_FOUND") }}</span>
                    </div>
                </div>

                <!-- Message: no response -->
                <div
                    class="row products-list-row"
                    v-if="
                        !(
                            productsSvc.isLoadingProducts ||
                            productsSvc.isLoadingMoreProducts
                        ) && productsSvc.itemData.ResponseStatus == 500
                    "
                    v-cloak
                >
                    <div
                        class="col-xs-offset-4 col-xs-8 col-sm-offset-2 col-sm-10 col-md-offset-0 col-md-12"
                    >
                        <span>{{ t("NO_SERVER_RESPONSE") }}</span>
                    </div>
                </div>

                <!-- Info: Records found count -->
                <div
                    class="row bold"
                    v-if="
                        !(
                            productsSvc.isLoadingProducts ||
                            productsSvc.isLoadingMoreProducts
                        ) &&
                        productsSvc.itemData.ResponseStatus == 200 &&
                        productsSvc.itemData.Items.length > 0
                    "
                    v-cloak
                >
                    <div
                        class="col-xs-offset-4 col-xs-8 col-sm-offset-2 col-sm-10 col-md-offset-0 col-md-12 text-normal"
                        style="padding-top: 1em"
                    >
                        <span
                            class="font-bold"
                            v-text="
                                productsSvc.getResultsListedCountStr(
                                    t('PRODUCTS_RECORD_LISTED'),
                                    t('PRODUCTS_RECORDS_LISTED'),
                                )
                            "
                        ></span>
                    </div>
                </div>

                <!-- Button "load more" -->
                <div
                    class="row"
                    v-if="
                        !(
                            productsSvc.isLoadingProducts ||
                            productsSvc.isLoadingMoreProducts
                        ) &&
                        productsSvc.hasMoreResults &&
                        productsSvc.itemData.ResponseStatus == 200
                    "
                    v-cloak
                >
                    <div
                        class="col-xs-offset-4 col-xs-8 col-sm-offset-2 col-sm-10 col-md-offset-0 col-md-12"
                        style="padding-top: 1.6em"
                    >
                        <a
                            class="btn-cb"
                            @click="
                                productsSvc.fetchMoreItems(
                                    productsSvc.searchFulltext,
                                    productsSvc.brand!.Code,
                                    productsSvc.skip,
                                    take,
                                    false,
                                    true,
                                    true,
                                )
                            "
                        >
                            {{ t("LOAD_MORE_ENTRIES") }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
